import React, { useState, useRef } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Checkbox, TextArea } from '@ygtechnologies/bygr-frontend-component-library';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { Button } from '@/components/Buttons';
import { TranslatableText } from '@/types';

export interface RejectDialogProps {
    dialogName: string;
    title: TranslatableText;
    show: boolean;
    onClose(): void;
    onReject(reason: string): Promise<any> | void;
    onSendRejectChange(v: boolean): void;
    sendRejectEmail?: boolean;
    sendRejectEmailLabel?: TranslatableText;
}

const RejectDialog: React.FC<RejectDialogProps> = ({
    title,
    show,
    onClose,
    onReject,
    dialogName,
    onSendRejectChange,
    sendRejectEmail,
    sendRejectEmailLabel,
}) => {
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);

    const focusElementRef = useRef<HTMLTextAreaElement>(null);

    const handleOpen = () => {
        setReason('');
        setSaving(false);

        if (focusElementRef.current) {
            focusElementRef.current.focus();
        }
    };

    const handleRejectClick = async () => {
        setSaving(true);

        try {
            await triggerHandler(onReject, reason);
            triggerHandler(onClose);
        } finally {
            setSaving(false);
        }
    };

    const handleCancelClick = () => {
        triggerHandler(onClose);
    };

    return (
        <Modal isOpen={show} toggle={handleCancelClick} onOpened={handleOpen}>
            <ModalHeader>{title}</ModalHeader>
            <ModalBody>
                <div className="form-group">
                    <label>
                        <Trans>Reason</Trans>
                    </label>
                    <TextArea
                        ref={focusElementRef}
                        value={reason}
                        name="reason"
                        className="form-control"
                        placeholder={t`Reason`}
                        onChange={(e) => setReason(e.target.value)}
                        autoResize
                        minRows={4}
                        maxRows={12}
                    />
                </div>
                {sendRejectEmail !== undefined && (
                    <Checkbox checked={sendRejectEmail} onChange={(e) => onSendRejectChange(e.target.checked)}>
                        {sendRejectEmailLabel}
                    </Checkbox>
                )}
            </ModalBody>
            <ModalFooter>
                <Button color="tertiary" onClick={handleCancelClick} disabled={saving}>
                    <Trans>Cancel</Trans>
                </Button>
                <Button color="danger" onClick={handleRejectClick} loading={saving}>
                    <Trans>Reject</Trans>
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default RejectDialog;